import React from "react";
import Media from "@qlik/qlik/src/components/Media";
import Cta from "@qlik/qlik/src/components/Cta";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import RichText from "@qlik/qlik/src/components/RichText";
import { getCardBodyContent } from "@qlik/qlik/src/lib/util/helpers";
import * as styles from "./styles.module.css";
import classNames from "classnames";

/**
 * DeckImageContent component
 * This component renders a section with image content.
 * @param {Object} data - The data for the section.
 */
const DeckImageContent = ({ data }) => {
    const {
        customId,
        id,
        title,
        description,
        additionalConfiguration,
        backgroundColor,
        backgroundImage,
        cardItems = [],
        cta = [],
        body,
        numberOfColumns,
    } = data ?? {};
    const sectionSpacingClassName = getSpacingClassName(additionalConfiguration, "py-15 md:py-20 lg:py-30");
    const { sectionColorMapping } = getColorClassName(backgroundColor);
    const numberOfCardColumns = numberOfColumns || 1;

    // eslint-disable-next-line react/no-unstable-nested-components, no-shadow
    const SingleCard = ({ data }) => {
        const descriptionClassName = `card-description mt-3.75 text-center line-clamp-6 ${sectionColorMapping.primaryTextColor}`;
        const bodyContent = getCardBodyContent(data, descriptionClassName);
        return (
            <div key={data.id} className="card-item mt-10 lg:mt-15">
                {/* Render the media if it exists */}
                <div className="max-h-[243px] min-h-60.75 rounded media-wrapper overflow-hidden h-full">
                    {data?.media?.[0] && <Media media={data?.media?.[0]} className="w-100 rounded" />}
                </div>
                {/* Render the card title if it exists */}
                {data?.title && (
                    <h3 className={`title-4 text-center mt-7.75 line-clamp-3 ${sectionColorMapping.primaryTextColor}`}>
                        {data?.title}
                    </h3>
                )}
                {/* Render the card description if it exists */}
                {bodyContent}
                {data.cta && (
                    <div className="col-12 pt-2.5 sm:pt-10">
                        <div className="text-center flex justify-center">
                            {data.cta?.map((button) => (
                                <Cta key={button.id} data={button} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Literally only setting the background image style
    const bgImageStyles = backgroundImage?.[0]?.secure_url && {
        backgroundImage: `url(${backgroundImage[0].secure_url.replace("/f_auto", "").replace("/q_auto", "")})`,
    };

    // Some cleanup for the section classes
    const sectionClasses = bgImageStyles
        ? classNames(styles.imageContentSection, sectionSpacingClassName, styles.bgImage)
        : classNames(styles.imageContentSection, sectionSpacingClassName);

    // Render the section with image content
    return (
        <section
            data-section
            id={customId || id}
            style={bgImageStyles}
            className={`image-content-section scroll-mt-45 ${sectionColorMapping.bgColor || ""} ${sectionClasses}`}
        >
            <div className="container">
                <div className="row">
                    <div className="section-content">
                        {/* Render the title if it exists */}
                        {title && (
                            <h2
                                className={`mb-5 title-2 text-center lg:tracking-tight10 ${sectionColorMapping.primaryTextColor}`}
                            >
                                {title}
                            </h2>
                        )}
                        {/* Render the description if it exists */}
                        {description?.description && (
                            <div className="text-center flex justify-center">
                                <p
                                    className={`text-md sm:text-2xl font-light leading-relaxed tracking-tight6 ${sectionColorMapping.primaryTextColor}`}
                                >
                                    {description?.description}
                                </p>
                            </div>
                        )}
                        {/* Render the cards */}
                        <div
                            className={`cards gap-x-7.5  pt-4.5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${numberOfCardColumns} `}
                        >
                            {cardItems?.map((card) => (
                                <SingleCard key={card.id} data={card} />
                            ))}
                        </div>
                        {/* Render the CTA if it exists and neither background nor icon content are present */}
                        {cta && (
                            <div className="col-12 pt-7.5 sm:pt-10 md:pt-25">
                                <div className="text-center flex justify-center">
                                    {cta?.map((button) => (
                                        <Cta key={button.id} data={button} />
                                    ))}
                                </div>
                            </div>
                        )}
                        {/* Render the body if it exists and neither background nor icon content are present */}
                        {body && (
                            <div className="col-12 pt-7.25">
                                <RichText data={data} className="text-center mt-0.75" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};
export default DeckImageContent;
